import { el, svg } from "../core/dom";
import { ACCORDION_NAME_MAX_CHARS } from "../appearance/constants";
import { part } from "../appearance/parts";
import type { AccordionStyles } from "../appearance/types";
import { COLLAPSE_EASE, MOTION, prefersReducedMotion } from "../shared/appearance";

/**
 * A short accessible name that starts with the visible title, so voice-control
 * users can still say what they see. Sliced by code point, not UTF-16 unit.
 */
export const briefName = (text: string): string => {
  const chars = Array.from(text.replace(/\s+/g, " ").trim());
  return chars.length <= ACCORDION_NAME_MAX_CHARS
    ? chars.join("")
    : `${chars.slice(0, ACCORDION_NAME_MAX_CHARS).join("").trimEnd()}…`;
};

const ICON_PATHS: Record<NoticeAccordionIcon, Array<[string, Record<string, string>]>> = {
  info: [
    ["circle", { cx: "12", cy: "12", r: "9" }],
    ["path", { d: "M12 16v-4M12 8h.01" }],
  ],
  shield: [
    ["path", { d: "M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3z" }],
    ["path", { d: "M9 12l2 2 4-4" }],
  ],
};

export type NoticeAccordionIcon = "info" | "shield";

export type CollapseRefs = {
  /** The slot the panel mounts into; stays in the tree while collapsed. */
  slot: HTMLElement;
  setOpen: (open: boolean) => void;
  isMounted: () => boolean;
  dispose: () => void;
};

/**
 * Animated disclosure body. Rows go 0fr -> 1fr so the panel eases to its
 * natural height without measuring it — notice copy varies a lot by language.
 *
 * Closing content is `inert` and `aria-hidden` for the length of the exit
 * animation, then unmounts, so from the moment it starts to close it is out of
 * the accessibility tree and the tab order.
 */
export const createCollapse = (
  content: HTMLElement,
  options: { id?: string; open: boolean; animate: () => boolean }
): CollapseRefs => {
  const slot = el("div", { style: { display: "contents" } });
  const inner = el("div", {
    style: {
      opacity: "1",
      transform: "none",
    },
  }, content);
  const clip = el("div", { style: { overflow: "hidden", minHeight: 0 } }, inner);
  const panel = el("div", { id: options.id, style: { display: "grid" } }, clip);
  let open = options.open;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let frame: number | null = null;

  const clearPending = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    if (frame !== null && typeof cancelAnimationFrame === "function") cancelAnimationFrame(frame);
    frame = null;
  };

  const paint = (expanded: boolean) => {
    const still = !options.animate();
    panel.style.gridTemplateRows = expanded ? "1fr" : "0fr";
    panel.style.transition = still ? "none" : `grid-template-rows ${COLLAPSE_EASE}`;
    inner.style.opacity = expanded ? "1" : "0";
    inner.style.transform = expanded ? "none" : "translateY(-4px)";
    inner.style.transition = still
      ? "none"
      : `opacity 260ms ${MOTION.outEase}, transform 320ms ${MOTION.outEase}`;
    inner.style.transitionDelay = expanded ? "80ms" : "0ms";
  };

  const mountOpen = (animated: boolean) => {
    panel.removeAttribute("inert");
    panel.removeAttribute("aria-hidden");
    if (panel.parentNode !== slot) slot.appendChild(panel);
    if (!animated || typeof requestAnimationFrame !== "function") {
      paint(true);
      return;
    }
    paint(false);
    // Two frames: the first commits the 0fr state, the second transitions
    // from it. One frame can be batched into the same paint and skip it.
    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        frame = null;
        paint(true);
      });
    });
  };

  if (open) mountOpen(false);

  return {
    slot,
    isMounted: () => panel.parentNode === slot,
    setOpen: (next: boolean) => {
      if (next === open) return;
      open = next;
      clearPending();
      if (next) {
        mountOpen(options.animate());
        return;
      }
      panel.setAttribute("inert", "");
      panel.setAttribute("aria-hidden", "true");
      paint(false);
      if (!options.animate()) {
        panel.remove();
        return;
      }
      timer = setTimeout(() => {
        timer = null;
        panel.remove();
      }, MOTION.collapseMs);
    },
    dispose: clearPending,
  };
};

export type NoticeAccordionOptions = {
  /** DOM id for the panel. */
  panelId: string;
  icon: NoticeAccordionIcon;
  title: string;
  /** Clamp the title to two lines — for when it is notice copy, not a label. */
  clampTitle?: boolean;
  /**
   * One-line preview shown under the title; clamped, so it can be the real copy.
   * Hidden from assistive tech, which would otherwise read the whole notice as
   * the button's name and then again inside the panel.
   */
  hint?: string;
  open: boolean;
  /** Held open by talkback: the header says so and ignores clicks. */
  locked: boolean;
  onToggle: () => void;
  styles: AccordionStyles;
  /** Whether disclosures animate: off with motion off or reduced motion. */
  animate: () => boolean;
  content: HTMLElement;
};

export type NoticeAccordionRefs = {
  element: HTMLElement;
  /** Whether keyboard focus sits inside the panel. */
  holdsFocus: (active: Element | null) => boolean;
  setState: (open: boolean, locked: boolean) => void;
  dispose: () => void;
};

/**
 * A collapsed-by-default section of the notice body (the notice text, the DPO
 * and grievance block). The header is a real button with `aria-expanded`, so
 * screen readers announce the state the chevron shows. The panel's content
 * unmounts once closed, so its links leave the tab order with it.
 */
export function renderNoticeAccordion(options: NoticeAccordionOptions): NoticeAccordionRefs {
  const { panelId, icon, title, clampTitle = false, hint, styles, animate } = options;
  let open = options.open;
  let locked = options.locked;

  const hintEl = hint
    ? el("span", { dir: "auto", style: styles.hint, "aria-hidden": "true" }, hint)
    : null;

  const chevron = svg(
    "svg",
    {
      width: "18",
      height: "18",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "2",
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
      "aria-hidden": "true",
      style: styles.chevron,
    },
    svg("path", { d: "M6 9l6 6 6-6" })
  );

  const head = el(
    "button",
    {
      type: "button",
      style: styles.head,
      "aria-label": clampTitle ? briefName(title) : null,
      onClick: () => {
        if (!locked) options.onToggle();
      },
    },
    el(
      "span",
      { style: styles.icon, "aria-hidden": "true" },
      svg(
        "svg",
        {
          width: "18",
          height: "18",
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          "stroke-width": "2",
          "stroke-linecap": "round",
          "stroke-linejoin": "round",
        },
        ...ICON_PATHS[icon].map(([tag, attrs]) => svg(tag, attrs))
      )
    ),
    el(
      "span",
      { style: styles.text },
      el(
        "span",
        { dir: "auto", style: { ...styles.title, ...(clampTitle ? styles.titleClamped : {}) } },
        title
      ),
      hintEl
    ),
    chevron
  );

  const body = el("div", { style: styles.body }, options.content);
  const collapse = createCollapse(body, { id: panelId, open, animate });

  const paintHead = () => {
    head.setAttribute("aria-expanded", String(open));
    if (open) {
      head.setAttribute("aria-controls", panelId);
    } else {
      head.removeAttribute("aria-controls");
    }
    if (locked) {
      head.setAttribute("aria-disabled", "true");
    } else {
      head.removeAttribute("aria-disabled");
    }
    if (hintEl) hintEl.style.display = open ? "none" : "";
    (chevron as unknown as HTMLElement).style.transform = open ? "rotate(180deg)" : "none";
    (chevron as unknown as HTMLElement).style.transition =
      animate() && !prefersReducedMotion()
        ? `transform ${MOTION.collapseMs}ms ${MOTION.sheetEase}`
        : "none";
  };
  paintHead();

  const element = el("div", { ...part("section"), style: styles.root }, head, collapse.slot);

  return {
    element,
    holdsFocus: (active) => Boolean(active && body.contains(active)),
    setState: (nextOpen, nextLocked) => {
      locked = nextLocked;
      if (nextOpen !== open) {
        open = nextOpen;
        collapse.setOpen(nextOpen);
      }
      paintHead();
    },
    dispose: () => collapse.dispose(),
  };
}
